import React, { useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import DashboardPanelHeader from "components/DashboardPanelHeader";
import LeaseListPopUp from "./leaseListPopUp";

const AssetTypeStackedChart = ({ data, isError, isLoading }) => {

    const [status, setStatus] = useState({});
    const [header, setHeader] = useState("");
    const [isPopup, setIsPopup] = useState(false);

    const handleClick = (assetType, seriesName) => {
        console.log("Clicked on:", assetType, seriesName);
        const filters = {};

        if (assetType && seriesName) {
            filters.assetType = assetType;
            filters.CurrentStage  = seriesName;
        }
        setStatus(filters);
        setHeader(`${assetType}`);
        setIsPopup(true);
    };

    const handleClose = () => setIsPopup(false);

    const chartData = data?.assetTypeSummary || [];
    const categories = chartData.map(item => item.assetType);
    const activeData = chartData.map(item => item.activeLeads || 0);
    const lostData = chartData.map(item => item.lostLeads || 0);
    const wonData = chartData.map(item => item.wonLeads || 0);

    const options = {
        chart: {
            type: "column",
            height: 420,
            backgroundColor: "transparent",
        },

        title: {
            text: "",
        },

        xAxis: {
            categories,
            lineColor: "#E4E4E4",
            labels: {
                style: {
                    fontFamily: "Outfit",
                },
            },
        },

        yAxis: {
            min: 0,
            allowDecimals: false,
            title: {
                text: "Leads",
                style: {
                    fontFamily: "Outfit",
                },
            },
            stackLabels: {
                enabled: true,
                style: {
                    fontFamily: "Outfit",
                    fontWeight: 600,
                },
            },
            gridLineColor: "#F1F1F1",
        },

        tooltip: {
            shared: true,
            useHTML: true,
            formatter: function () {
                const category = this.points?.[0]?.series?.xAxis?.categories?.[this.x];
                return `
                        <div style="font-family:Outfit;font-size:12px">
                            <b>${category}</b><br/>
                            ${this.points.map(p => `
                            <span style="color:${p.color}">●</span> 
                            ${p.series.name}: ${p.y}
                            `).join("<br/>")}
                            <br/><b>Total: ${this.points.reduce((sum, p) => sum + p.y, 0)}</b>
                        </div>
                        `;
            }
        },

        plotOptions: {
            column: {
                stacking: "normal",
                borderRadius: 4,
                cursor: "pointer",
                point: {
                    events: {
                        click: function () {
                            const assetType = this.category;
                            const seriesName = this.series.name;
                            handleClick(assetType, seriesName);
                        }
                    }
                }
            },
        },

        series: [
            {
                name: "Active",
                data: activeData,
                color: "#3B82F6",
            },
            {
                name: "Lost",
                data: lostData,
                color: "#DC2626",
            },
            {
                name: "Won",
                data: wonData,
                color: "#22C55E",
            },
        ],

        credits: { enabled: false },

        legend: {
            align: "center",
            verticalAlign: "bottom",
            itemStyle: {
                fontFamily: "Outfit",
            },
        },
    };

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    border: "1px solid #E4E4E4",
                    borderRadius: 1,
                    width: "100%",
                }}
            >
                <DashboardPanelHeader eyebrow="Asset demand" title="Lead Mix by Asset Type" subtitle="Compare active, won, and lost demand across asset categories." />
                <Box
                    sx={{
                        borderBottom: "1px solid #E4E4E4",
                        p: 1.5,
                        display: "none",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: "Outfit",
                            fontWeight: 600,
                            fontSize: "20px",
                        }}
                    >
                        Asset Type Wise Leads
                    </Typography>

                    <Typography
                        sx={{
                            fontFamily: "Outfit",
                            fontWeight: 300,
                            fontSize: "14px",
                            color: "#7D7F81",
                        }}
                    >
                        Distribution of total, active, lost and won leads across asset types
                    </Typography>
                </Box>

                {isLoading && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <CircularProgress size={32} />
                    </Box>
                )}
                {isError && (
                    <Box
                        sx={{
                            minHeight: "100px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                color: "#FF4F8B",
                                textAlign: "center",
                            }}
                        >
                            Failed to load data. Please try again.
                        </Typography>
                    </Box>
                )}

                {!isError && !isLoading && <Box sx={{ p: 2 }}>
                    <HighchartsReact highcharts={Highcharts} options={options} />
                </Box>}
            </Box>
            {
                isPopup && (
                    <LeaseListPopUp
                        header={header}
                        open={isPopup}
                        handleClose={handleClose}
                        filter={status}
                    />
                )
            }
        </>
    );
};

export default AssetTypeStackedChart;
